import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled, { keyframes, css } from 'styled-components';
import { Link } from 'react-router-dom';
import { menu, cross, logo } from '../utils/icons';
import { textGray } from '../utils/colors';
import { TinyTitle, TinyText, BaseLink } from './CommonUiComponents';

const animationTimer = 300;
const easer = 'cubic-bezier(0.4, 0, 0.2, 1)';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
  }
`;

const slideIn = keyframes`
  from {
    left: calc(-100vw - 10rem);
  }

  to {
    left: 0;
  }
`;

const slideOut = keyframes`
  from {
    left: 0;
  }

  to {
    left: calc(-100vw - 10rem);
  }
`;

const Toggle = styled.a`
  display: block;
  position: fixed;
  top: 1.5rem;
  left: 1.5rem;
  z-index: 12;

  svg {
    mix-blend-mode: exclusion;
  }

  g {
    transition: stroke ${animationTimer}ms ${easer};
    stroke: ${props => (props.inverted ? textGray : '#fff')};
  }

  @media screen and (max-width: 64rem) {
    height: 3.5rem;
    background-color: #ffffff;
    top: 0;
    left: 0;
    width: 4.625rem;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.4);
    display: flex;
    justify-content: center;
    align-items: center;

    ${props =>
      props.open &&
      css`
        box-shadow: none;
      `};
  }
`;

const MenuWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
`;

const Fader = MenuWrapper.extend`
  background-color: rgba(255, 255, 255, 0.6);
  transition: background-color ${animationTimer}ms ${easer};
  animation: ${fadeIn} ${animationTimer}ms ${easer};

  ${props =>
    props.fadeout &&
    css`
      animation: ${fadeOut} ${animationTimer}ms ${easer};
    `};
`;

const StyledMenu = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  background: #fff;
  box-shadow: 0 2px 30px 0 rgba(0, 0, 0, 0.5);
  width: calc(100vw - 10rem);
  animation: ${slideIn} ${animationTimer}ms ${easer};
  overflow-x: hidden;
  overflow-y: auto;
  z-index: 11;
  display: flex;
  padding-left: 7rem;
  padding-right: 5rem;
  -webkit-overflow-scrolling: touch;

  ${props =>
    props.fadeout &&
    css`
      animation: ${slideOut} ${animationTimer}ms ${easer};
    `};

  @media screen and (max-width: 73rem) {
    flex-direction: column;
  }

  @media screen and (max-width: 50rem) {
    width: calc(100vw - 2rem);
    padding-left: 3.5rem;
    padding-right: 2.5rem;
  }

  @media screen and (max-width: 23rem) {
    width: calc(100vw - 1rem);
  }
`;

const Section = styled.div`
  padding: 0 1.5rem;
  margin: 1.5rem 0;
  display: flex;
`;

const Part = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  justify-content: space-between;

  &:last-child {
    ${Section} {
      @media screen and (min-width: 73rem) {
        justify-content: flex-end;
      }
    }
  }

  ${props =>
    props.fixed &&
    css`
      max-width: 23rem;
    `};

  ${props =>
    props.hug &&
    css`
      @media screen and (max-width: 50rem) {
        margin-top: auto;
      }
    `};

  @media screen and (max-width: 73rem) {
    flex: none;
  }
`;

const Logo = styled.i`
  display: block;
  margin-top: -0.2rem;

  > svg {
    width: 9.8125rem;
  }

  .cls-1,
  .cls-2 {
    fill: #cc1719;
  }
`;

const NavLink = styled(Link)`
  display: block;
  color: #1d262a;
  text-decoration: none;
  font-weight: 600;
  font-size: 2.25rem;
  margin: 1.25rem 0;

  &:hover {
    text-decoration: underline;
  }

  @media screen and (max-width: 50rem) {
    font-size: 1.5rem;
    margin: 0.8rem 0;
  }

  @media screen and (max-width: 23rem) {
    font-size: 1.2rem;
    margin: 0.6rem 0;
  }
`;

const Nav = styled.nav`
  display: flex;
  flex-direction: column;

  > ${NavLink}:last-child {
    margin-top: 6rem;
    margin-bottom: 6rem;
    font-weight: 300;

    @media screen and (max-width: 50rem) {
      margin-top: 3rem;
      margin-bottom: 3rem;
    }
  }
`;

const Contact = styled.div`
  display: flex;
  justify-content: space-between;
  max-width: 40rem;

  @media screen and (max-width: 73rem) {
    margin-bottom: 4rem;
  }

  @media screen and (max-width: 50rem) {
    margin-bottom: 2rem;
  }

  @media screen and (max-width: 36rem) {
    flex-direction: column;
  }
`;

const ContactColumn = styled.div`
  &:not(:last-child) {
    padding-right: 3rem;
  }

  @media screen and (max-width: 36rem) {
    &:last-child {
      margin-top: 2rem;
    }
  }
`;

class Menu extends Component {
  componentDidMount() {
    document.addEventListener('keyup', this.closeOnEscape);
  }

  componentWillUnmount() {
    document.removeEventListener('keyup', this.closeOnEscape);
  }

  closeOnEscape = e => {
    const { onMenuToggle } = this.props;
    if (e.keyCode === 27) onMenuToggle();
  };

  handleLogout = () => {
    const { onMenuToggle, logoutHandler } = this.props;

    logoutHandler();
    onMenuToggle();
  };

  render() {
    const { open, bookID, onMenuToggle } = this.props;

    return (
      <StyledMenu fadeout={!open}>
        <Part fixed>
          <Section>
            <BaseLink to={`/bok/${bookID}`} onClick={onMenuToggle}>
              <Logo>{logo}</Logo>
            </BaseLink>
          </Section>
          <Section>
            <Nav>
              <NavLink
                onClick={onMenuToggle}
                to={`/bok/${bookID}/om-mitt-valg`}
              >
                Om MITT VALG!
              </NavLink>
              <NavLink onClick={onMenuToggle} to={`/bok/${bookID}`}>
                Deloversikt
              </NavLink>
              <NavLink onClick={onMenuToggle} to={`/bok/${bookID}/min-side`}>
                Min side
              </NavLink>
              <NavLink onClick={onMenuToggle} to={`/bok/${bookID}/kontakt-oss`}>
                Kontakt
              </NavLink>
              <NavLink
                onClick={this.handleLogout}
                to={`/bok/${bookID}/logg-inn`}
              >
                Logg ut
              </NavLink>
            </Nav>
          </Section>
        </Part>
        <Part hug>
          <Section>
            <Contact>
              <ContactColumn>
                <TinyTitle>Kontakt</TinyTitle>
              </ContactColumn>
              <ContactColumn>
                <TinyText>Ensjøveien 18</TinyText>
                <TinyText>0661 Oslo</TinyText>
                <TinyText spacer>Tlf: 23 24 46 35</TinyText>
                <TinyText>Fax: 23 24 46 39</TinyText>
              </ContactColumn>
              {/*<ContactColumn>
                <TinyText inverted>
                  <strong>Kursleder</strong>
                </TinyText>
                <TinyText inverted>Navn Navnesen</TinyText>
                <TinyText inverted>000 00 000</TinyText>
                <TinyText inverted>000 00 000</TinyText>
                <TinyText inverted>navn.navnesen@mittvalg.no</TinyText>
              </ContactColumn>*/}
            </Contact>
          </Section>
          <Section>
            <TinyText>
              Copyright {new Date().getFullYear()} Stiftelsen DET ER MITT VALG –
              Et Lions-program
            </TinyText>
          </Section>
        </Part>
      </StyledMenu>
    );
  }
}

Menu.propTypes = {
  open: PropTypes.bool.isRequired,
  onMenuToggle: PropTypes.func.isRequired,
  bookID: PropTypes.number.isRequired
};

class MainMenu extends Component {
  state = {
    mountMenu: this.props.open
  };

  unmountTimeout = null;

  componentWillUnmount = () => {
    window.clearTimeout(this.unmountTimeout);
    document.removeEventListener('keyup', this.endEditingOnEscape);
  };

  unmountMenu = () => {
    this.unmountTimeout = window.setTimeout(
      () => this.setState({ mountMenu: false }),
      animationTimer - 10
    );
  };

  toggleMenu = () => {
    const { open, onMenuButtonClick } = this.props;
    const { mountMenu } = this.state;

    onMenuButtonClick();

    if (mountMenu) window.clearInterval(this.unmountTimeout);

    if (open) {
      this.unmountMenu();
    } else {
      this.setState({ mountMenu: true });
    }
  };

  render() {
    const { open, invertedTheme, isOnWhitePage, bookID, logout } = this.props;
    const { mountMenu } = this.state;

    return (
      <div>
        <Toggle
          open={open}
          inverted={!invertedTheme || isOnWhitePage || open}
          href="#"
          onClick={e => {
            e.preventDefault();
            this.toggleMenu();
          }}
        >
          {open ? cross : menu}
        </Toggle>
        {mountMenu && (
          <MenuWrapper>
            <Menu
              open={open}
              onMenuToggle={this.toggleMenu}
              bookID={bookID}
              logoutHandler={logout}
            />
            <Fader fadeout={!open} onClick={this.toggleMenu} />
          </MenuWrapper>
        )}
      </div>
    );
  }
}

MainMenu.propTypes = {
  open: PropTypes.bool.isRequired,
  invertedTheme: PropTypes.bool.isRequired,
  isOnWhitePage: PropTypes.bool.isRequired,
  onMenuButtonClick: PropTypes.func.isRequired,
  bookID: PropTypes.number.isRequired
};

export default MainMenu;



// WEBPACK FOOTER //
// ./src/components/MainMenu.js